import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);

// Axios with credentials so session_token cookie is sent on all calls
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("kb_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    const t = localStorage.getItem("kb_token");
    // Try /auth/me either via cookie or via stored JWT Bearer
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    axios.get(`${API}/auth/me`, { headers, withCredentials: !t })
      .then(r => setUser(r.data))
      .catch(() => { localStorage.removeItem("kb_token"); setToken(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("kb_token", data.token);
    setToken(data.token); setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("kb_token", data.token);
    setToken(data.token); setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    }); } catch {}
    localStorage.removeItem("kb_token");
    setToken(null); setUser(null);
  };

  const setSessionUser = (u) => {
    // Used by AuthCallback after Emergent Google session exchange
    setUser(u);
    // We don't store the Emergent session_token in localStorage — cookie carries it.
    // Clear stale JWT.
    localStorage.removeItem("kb_token");
    setToken(null);
  };

  const authHeader = () => token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, register, logout, setSessionUser, authHeader, API }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
